import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import {
  getComparisonOperators,
  getAlertImportances,
  getAlertRecurrsEveryIntervals,
  getAlertCoveredPeriodIntervals,
  getAlertVariantCoveredPeriodIntervals,
  notificationDurationMiddle,
  collectParametersFromRules,
} from "../../lib/utils";
import { formatQuery } from "react-querybuilder";
import { AggregateFunction } from "../../lib/aggregatefunctions";
import { addNotification } from "./notificationsslice";

const defaultDataset = {
  Id: 0,
  Title: "None",
  Description: "",
  CategoryId: 0,
  Alerts: [],
  Fields: [],
  Users: [],
  Where: [],
  OrderId: -1,
};

const defaultDatasetId = 0;

const defaultFormInitialValues = {
  Id: 0,
  DatasetId: defaultDatasetId,
  Title: "",
  GroupedFields: [],
  IsActive: true,
  WhereObject: "",
  WhereStatement: "",
  WhereParameters: "",
  Viewers: [],
  Groups: [],
  RowsAffected: 0,
  SampleData: [],
  FieldId: null,
  Fields: [],
  IsSystem: false,

  groupByOpen: false,
  filterOpen: false,
  updateSection: false,
  showEditSection: false,
  gridSection: true,
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  currentDataset: defaultDataset,
  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formServerErrors: [],
  lookups: {
    datasets: [],
  },
  panes: [
    {
      // size: "30%",
      size: "22%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "75%",
      max: "80%",
      collapsible: false,
    },
  ],
  fieldsDialogOpen: false,
  fieldsToAdd: [],
};

const dataCorrectionPageSlice = createSlice({
  name: "dataCorrectionPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }), //no payload

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }), //payload: {id: , value:}

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    setFieldValue: (state, action) => {
      //payload is the field - sending a field => replace the field with that id with the passed field's properties
      const pageState = current(state);
      const entity = action.payload;

      return {
        ...state,
        formValues: {
          ...state.formValues,
          Fields: state.formValues.Fields.map((item) => {
            if (item.FieldId == entity.FieldId) return { ...entity };
            else return item;
          }),
        },
      };
    },

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => {
      //payload: {Datasets: includes the default one  }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        mode: 0,
        currentDataset: defaultDataset,
        formKey: new Date().toString(),
        formValues: { ...defaultFormInitialValues },
        formInitialValues: { ...defaultFormInitialValues },
        formServerErrors: [],
        lookups: { datasets: action.payload.Datasets },
      };
    },

    datasetLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      let currentDataset = pageState.lookups.datasets.find(
        (e) => e.Id == action.payload.datasetId,
      );

      //aggregate expressions - already sorted by title by api
      let aggregateExpressions = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == true &&
            e.IsAggregate == true &&
            e.DataTypeId == 2,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //aggregateExpressions = sortArrayByString(aggregateExpressions, "Title");
      aggregateExpressions = [
        { Id: null, Title: "None", Name: "None" },
        ...aggregateExpressions,
      ];

      //fields - already sorted by title by api
      //let fields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.DataTypeId == 2)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name}));
      let fields = action.payload.fields
        .filter((e) => e.IsExpression == false && e.IsAggregate == false)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          DataTypeId: item.DataTypeId,
        }));
      //fields = sortArrayByString(fields, "Title");
      fields = [
        { Id: null, Title: "None", Name: "None", DataTypeId: 0 },
        ...fields,
      ];

      //time fields - already sorted by title by api
      let timeFields = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == false &&
            e.IsAggregate == false &&
            e.DataTypeId == 5 &&
            e.IsUTC == true,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //timeFields = sortArrayByString(timeFields, "Title");
      timeFields = [{ Id: 0, Title: "None", Name: "None" }, ...timeFields];

      //users - already sorted by name by api
      let users = action.payload.users.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        IsActive: item.IsActive,
        Checked: false,
      }));
      //users = sortArrayByString(users, "Name");

      //groups - already sorted by name by api
      let groups = action.payload.groups.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        Checked: false,
      }));

      //groupby fields - fields excluding is expression and excluding is aggregate and those that are not groupable in alert
      let groupByFields = action.payload.fields
        .filter((e) => e.IsAggregate == false && e.IsGroupableInAlert == true)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          Checked: false,
        }));
      //let groupByFields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.IsGroupableInAlert == true)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name, Checked: false}));

      //where
      let where = action.payload.where;
      for (let prop in where) {
        //must add name and label (lowercases) to every field
        where[prop].name = where[prop].Name;
        where[prop].label = where[prop].Title;

        if (where[prop].DataTypeId == 1) {
          where[prop].inputType = "bool";
          //where[prop].dataType="bool";
        } else if (where[prop].DataTypeId == 2) {
          where[prop].inputType = "number";
          //where[prop].dataType="number";
        } else if (where[prop].DataTypeId == 3) {
          where[prop].inputType = "string";
          //where[prop].dataType="string";
        } else if (where[prop].DataTypeId == 4 || where[prop].DataTypeId == 5) {
          where[prop].inputType = "date";
        }

        if (where[prop].IsLookup) {
          for (let p in where[prop].values) {
            where[prop].values[p].label = where[prop].values[p].Title;
          }
        }
      }

      return {
        ...state,
        processing: false,
        processingText: "",
        mode: 2,
        currentDataset: {
          ...currentDataset,
          Fields: [...action.payload.fields],
          Users: [...action.payload.users],
          Groups: [...action.payload.groups],
          Where: where,
          Lookups: {
            AggregateExpressions: aggregateExpressions,
            Fields: fields,
            AggregateFunctions: [
              { Id: 0, Title: "None", Name: "None" },
              ...AggregateFunction.functions
                .sort((a, b) =>
                  a.title < b.title ? -1 : a.title == b.title ? 0 : 1,
                )
                .map((e) => ({
                  Id: e.id,
                  Title: e.title,
                  Name: e.name,
                  Description: e.description,
                  AppliesTo: e.appliesTo,
                })),
            ], //getAggregateFunctions(),
            Operators: getComparisonOperators(),
            AlertImportances: getAlertImportances(),
            AlertRecurrsEveryIntervals: getAlertRecurrsEveryIntervals(),
            AlertCoveredPeriodIntervals: getAlertCoveredPeriodIntervals(),
            AlertVariantCoveredPeriodIntervals:
              getAlertVariantCoveredPeriodIntervals(),
            TimeFields: timeFields,
            Users: users,
            Groups: groups,
            GroupByFields: groupByFields,
          },
        },
        formKey: new Date().toString(),
        formInitialValues: { ...defaultFormInitialValues },
        formValues: {
          ...state.formValues,
          DatasetId: action.payload.datasetId,
          Viewers: users,
          Groups: groups,
          GroupedFields: groupByFields,
          groupByOpen: true,
          filterOpen: true,
          showEditSection: false,
          Fields: [],
          // WhereObject: {
          //   ...state.formValues.WhereObject,
          //   rules: [],
          // },
        },
        formServerErrors: [],
      };
    },

    dataGridLoaded: (state, action) => {
      //payload: { datasetId: , alerts: [], fields: [], users: [], groups: [], where: []}
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        processing: false,
        processingText: "",
        mode: 2,
        formKey: new Date().toString(),
        formInitialValues: { ...defaultFormInitialValues },
        formValues: {
          ...state.formValues,
          RowsAffected: action.payload.RowsAffected,
          SampleData: action.payload.SampleData,
          groupByOpen: false,
          filterOpen: false,
          updateSection: true,
          showEditSection: true,
        },
        formServerErrors: [],
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      let entityId = action.payload;
      let newCurrentDataset = { ...pageState.currentDataset };

      //compute currentDataset, IF it contains the removed entity, by removing the alert from it
      if (newCurrentDataset.Alerts.find((e) => e.Id == entityId))
        newCurrentDataset = {
          ...newCurrentDataset,
          Alerts: newCurrentDataset.Alerts.filter((e) => e.Id != entityId),
        };

      //if it's the current entity => goto no mode
      let currentMode = {};
      if (pageState.mode == 2 && pageState.formValues.Id == entityId)
        currentMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: {
            ...defaultFormInitialValues,
            // DatasetId: pageState.currentDataset.Id,
            // Viewers: [...pageState.currentDataset.Lookups.Users],
            // GroupedFields: [...pageState.currentDataset.Lookups.GroupByFields]
          },
          formValues: {
            ...defaultFormInitialValues,
            // DatasetId: pageState.currentDataset.Id,
            // Viewers: [...pageState.currentDataset.Lookups.Users],
            // GroupedFields: [...pageState.currentDataset.Lookups.GroupByFields]
          },
          formServerErrors: [],
        };

      return { ...state, currentDataset: newCurrentDataset, ...currentMode };
    },

    dataFromDatasetDeleted: (state, action) => {
      //payload: id of the deleted entity
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      //todo
      return {
        ...state,
        processing: false,
        processingText: "",
      };
    },

    dataFromDatasetUpdated: (state, action) => {
      //payload: id of the deleted entity
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      //todo
      return {
        ...state,
        processing: false,
        processingText: "",
      };
    },

    //non-computed fields dialog
    openFieldsDialog: (state, action) => {
      const pageState = current(state);

      let fieldsToAdd = pageState.currentDataset.Fields //they are sorted by title since when the dataset is loaded
        //cannot filter because i'll need to include a fields more than once if i want to apply different agg. fun. on the same field .filter(e => !pageState.formValues.Fields.find(f => f.FieldId == e.Id))
        .map((item, index) => ({
          Id: 0,
          Title: item.Title,
          Name: item.Title,
          IsComputedField: false,
          FunctionId: 0,
          FieldId: item.Id,
          IsGrouped: false,
          ParentFieldId: null,
          ExpressionBody: "",
          ExpressionBodyResolved: "",
          DataTypeId: item.DataTypeId,
          IsDecimal: item.IsDecimal,
          IsPercent: item.IsPercent,
          HasAggregateFunction: false,
          HasComputedFunction: false,
          HasExpressionBody: false,
          FieldIsAggregate: item.IsAggregate,
          FieldIsExpression: item.IsExpression,
          Checked: false,
          IsVisible: true,
        }));

      return {
        ...state,
        fieldsToAdd: [...fieldsToAdd],
        fieldsDialogOpen: true,
      };
    },

    closeFieldsDialog: (state, action) => ({
      ...state,
      fieldsToAdd: [],
      fieldsDialogOpen: false,
    }),

    setFieldChecked: (state, action) => ({
      ...state,
      fieldsToAdd: state.fieldsToAdd.map((item) => {
        //payload: FieldId
        if (item.FieldId == action.payload)
          return { ...item, Checked: !item.Checked };
        else return { ...item };
      }),
    }),

    clearFields: (state, action) => ({
      ...state,
      formValues: { ...state.formValues, Fields: [] },
    }),

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else
        return {
          ...state,
          pageDisposed: false,
        };
    },
  },
});

export default dataCorrectionPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "datacorrection/getdatacorrectionpagedata",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().dataCorrectionPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    //add the 0/None dataset

    data.Datasets.splice(0, 0, { ...defaultDataset });

    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
        groups: [],
      }),
    );
    return;
  }

  try {
    var response = await api({
      url: "datacorrection/getdatacorrectionbydatasetid",
      method: "post",
      data: {
        datasetId: datasetId,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().dataCorrectionPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      datasetLoaded({
        datasetId: datasetId,
        fields: data.Fields,
        users: data.Users,
        groups: data.SecurityGroups,
        where: data.Where,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDataGrid = (datasetId, t) => async (dispatch, getState) => {
  const pageState = getState().dataCorrectionPage;
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
      }),
    );
    return;
  }
  //some tramsformation is needed
  // let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  // let whereStatement = "";
  // let whereParameters = [];

  // let parameters = formatQuery(
  //   pageState.formValues.WhereObject,
  //   "parameterized"
  // ).params;

  // if (
  //   (parameters != null && parameters.length > 0) ||
  //   (pageState.formValues.WhereObject.rules &&
  //     pageState.formValues.WhereObject.rules.length > 0)
  // ) {
  //   whereStatement = formatQuery(
  //     pageState.formValues.WhereObject,
  //     "parameterized"
  //   ).sql;
  //   whereParameters = parameters;
  // }

  //some tramsformation is needed
  let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  let whereStatement = "";
  let whereParameters = [];

  // generate parameterized SQL
  const formatted = formatQuery(
    pageState.formValues.WhereObject,
    "parameterized",
  );

  whereParameters =
    pageState.formValues.WhereObject?.rules?.length > 0
      ? collectParametersFromRules(pageState.formValues.WhereObject.rules)
      : [];

  if (
    (whereParameters != null && whereParameters.length > 0) ||
    (pageState.formValues.WhereObject.rules &&
      pageState.formValues.WhereObject.rules.length > 0)
  ) {
    whereStatement = formatted.sql;
  }

  let entity = {
    DatasetId: datasetId,
    WhereObject: whereObject,
    WhereStatement: whereStatement,
    GroupedFields: pageState.formValues.GroupedFields.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ FieldId: item.Id })),
    Parameters: whereParameters,
  };

  try {
    var response = await api({
      url: "datacorrection/getfiltereddataperdataset",
      method: "post",
      data: { ...entity },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().dataCorrectionPage.pageDisposed) return;

    let data = response.data.Data;
    // let data = {
    //   NbOfRowAffected: 15,
    //   SampleData: productSampleData,
    // };

    dispatch(
      dataGridLoaded({
        DatasetId: datasetId,
        RowsAffected: data.NbOfRowAffected,
        SampleData: data.SampleData,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteDataFromDataset = (t) => async (dispatch, getState) => {
  const pageState = getState().dataCorrectionPage;
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  let datasetId = pageState.currentDataset.Id;
  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
      }),
    );
    return;
  }

  //some tramsformation is needed
  let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  let whereStatement = "";
  let whereParameters = [];

  // generate parameterized SQL
  const formatted = formatQuery(
    pageState.formValues.WhereObject,
    "parameterized",
  );

  whereParameters =
    pageState.formValues.WhereObject?.rules?.length > 0
      ? collectParametersFromRules(pageState.formValues.WhereObject.rules)
      : [];

  if (
    (whereParameters != null && whereParameters.length > 0) ||
    (pageState.formValues.WhereObject.rules &&
      pageState.formValues.WhereObject.rules.length > 0)
  ) {
    whereStatement = formatted.sql;
  }
  // //some tramsformation is needed
  // let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  // let whereStatement = "";
  // let whereParameters = [];

  // let parameters = formatQuery(
  //   pageState.formValues.WhereObject,
  //   "parameterized",
  // ).params;

  // if (
  //   (parameters != null && parameters.length > 0) ||
  //   (pageState.formValues.WhereObject.rules &&
  //     pageState.formValues.WhereObject.rules.length > 0)
  // ) {
  //   whereStatement = formatQuery(
  //     pageState.formValues.WhereObject,
  //     "parameterized",
  //   ).sql;
  //   whereParameters = parameters;
  // }

  let entity = {
    DatasetId: datasetId,
    WhereObject: whereObject,
    WhereStatement: whereStatement,
    GroupedFields: pageState.formValues.GroupedFields.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ FieldId: item.Id })),
    Parameters: whereParameters,
  };

  try {
    var response = await api({
      url: "datacorrection/deletefiltereddatafromdataset",
      method: "post",
      data: { ...entity },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().dataCorrectionPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      dataFromDatasetDeleted({
        DatasetId: datasetId,
        RowsAffected: data.NbOfRowAffected,
        SampleData: data.SampleData,
      }),
    );

    dispatch(
      addNotification({
        text: t("slices.notifyData"),
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const updateDataFromDataset = (t) => async (dispatch, getState) => {
  const pageState = getState().dataCorrectionPage;
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  let datasetId = pageState.currentDataset.Id;
  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
      }),
    );
    return;
  }

  //some tramsformation is needed
  let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  let whereStatement = "";
  let whereParameters = [];

  // generate parameterized SQL
  const formatted = formatQuery(
    pageState.formValues.WhereObject,
    "parameterized",
  );

  whereParameters =
    pageState.formValues.WhereObject?.rules?.length > 0
      ? collectParametersFromRules(pageState.formValues.WhereObject.rules)
      : [];

  if (
    (whereParameters != null && whereParameters.length > 0) ||
    (pageState.formValues.WhereObject.rules &&
      pageState.formValues.WhereObject.rules.length > 0)
  ) {
    whereStatement = formatted.sql;
  }
  // //some tramsformation is needed
  // let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  // let whereStatement = "";
  // let whereParameters = [];

  // let parameters = formatQuery(
  //   pageState.formValues.WhereObject,
  //   "parameterized",
  // ).params;

  // if (
  //   (parameters != null && parameters.length > 0) ||
  //   (pageState.formValues.WhereObject.rules &&
  //     pageState.formValues.WhereObject.rules.length > 0)
  // ) {
  //   whereStatement = formatQuery(
  //     pageState.formValues.WhereObject,
  //     "parameterized",
  //   ).sql;
  //   whereParameters = parameters;
  // }

  const transformedFields = pageState.formValues.Fields.map((field) => {
    const datasetField = pageState.currentDataset.Fields.find(
      (datasetField) => datasetField.Id === field.FieldId,
    );

    return {
      Id: field.FieldId,
      Name: datasetField ? datasetField.Name : "Unknown",
      Value: field.IsEquation
        ? `[${field.UsedField?.Name || "Unknown"}] ${field.Operator || ""} ${
            field.Value || ""
          }`
        : field.Value || "",
    };
  });

  let entity = {
    DatasetId: datasetId,
    WhereObject: whereObject,
    WhereStatement: whereStatement,
    GroupedFields: pageState.formValues.GroupedFields.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ FieldId: item.Id })),
    Parameters: whereParameters,
    FieldsToUpdate: transformedFields,
  };

  console.log("transformedFields", transformedFields);

  try {
    var response = await api({
      url: "datacorrection/updatefiltereddatafromdataset",
      method: "post",
      data: { ...entity },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().dataCorrectionPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      dataFromDatasetUpdated({
        DatasetId: datasetId,
        RowsAffected: data.NbOfRowAffected,
      }),
    );

    dispatch(
      addNotification({
        text: t("slices.notifyData"),
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  pageLoaded,
  setPanes,
  datasetLoaded,
  createEntity,
  setFormValue,
  setFormServerErrors,
  entityLoaded,
  removeEntityFromUI,
  entityInserted,
  entityUpdated,
  entityCloned,
  entityDeleted,
  setFieldValue,
  dataGridLoaded,
  openFieldsDialog,
  setFieldChecked,
  closeFieldsDialog,
  clearFields,
  dataFromDatasetDeleted,
  dataFromDatasetUpdated,
  disposePage,
} = dataCorrectionPageSlice.actions;
